// Writer Studio Constants
import { ChapterVitals, FrontMatter, BackMatter, PublishingEssentials, AuthorDetails } from './types';

export const LOCAL_STORAGE_ACTIVE_PROJECT_KEY = 'writerStudioActiveProject_v3';
export const LOCAL_STORAGE_SAVED_PROJECTS_LIST_KEY = 'writerStudioSavedProjectsList_v1';
export const LOCAL_STORAGE_API_KEY_KEY = 'writerStudioApiKey';

// CSS classes for consistent styling
export const labelClass = "block text-sm font-medium text-gray-700 mb-1";
export const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500";
export const textareaClass = "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-vertical";
export const primaryButtonClass = "bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
export const secondaryButtonClass = "bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
export const dangerButtonClass = "bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
export const actionButtonClass = "bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed";

export const initialChapterVitals: ChapterVitals = {
    chapterStartConcept: '',
    chapterEndConcept: '',
    objective: '',
    keyCharactersInChapter: '',
    temparoryCharactersInChapter: '', 
    intenseScenes: '',
    characterEvolutionInChapter: '',
    keyLocationsInChapter: '',
    culturalElementsInChapter: '',
    authorsDirection: '',
};

export const initialAuthorDetails: AuthorDetails = {
    authorName: '',
    authorBio: '',
    authorWebsite: '',
    authorSocialMedia: '',
    authorContact: '',
    authorProfileImageBase64: null,
    copyrightYear: '',
    isbn: '',
    publisher: '',
    edition: '',
    copyrightNotice: '',
};

export const initialFrontMatter: FrontMatter = {
    content: '',
    titlePageContent: '',
    titlePageImageBase64: null,
    titleImagePrompt: '',
    titleImageAspectRatio: '',
    titleImageBase64: null,
    copyrightPageContent: `© ${new Date().getFullYear()} [Author Name/Publisher Name]. All rights reserved.`,
    dedication: '',
    epigraph: '',
    tableOfContents: '<!-- Table of Contents will be generated here -->', // Default placeholder
    foreword: '',
    forewordBy: '',
    preface: '',
    acknowledgmentsFront: '',
    prologue: '',
    introduction: '',
}

export const initialBackMatter: BackMatter = {
    content: '',
    epilogue: '',
    afterword: '',
    authorsNote: '',
    acknowledgmentsBack: '',
    appendices: [],
    glossary: [],
    bibliography: [],
    index: '',
    readingGroupGuide: '',
    teaserNextBook: '',
    aboutTheAuthor: '',
    contactSocialMedia: '',
    backCoverImageBase64: null,
    backCoverImagePrompt: '',
    backCoverImageAspectRatio: '',
    backCoverImageGeneratedBase64: null,
};

export const initialPublishingEssentials: PublishingEssentials = {
    metadataFileNotes: '',
    queryLetterDraft: '',
    marketingBlurb: '',
    coverDesignBrief: '',
    isbnNotes: '',
    copyrightRegistrationNotes: '',
    mediumFormat: 'both',
};

export const initialBookMetadata = {
    // Core Book Info (user-editable) - NEW SEGREGATED FIELDS
    title: '',
    subtitle: '',
    blurb: '',
    overallMood: '', // For cover generation
    // Publishing Details
    genreSubject: '',
    fictionVsNonFiction: '',
    audienceAgeBand: '',
    targetWordCount: undefined,
    // Narrative Structure
    narrativePointOfView: '',
    tense: '',
    voiceStyle: '',
    structure: '',
    settingTime: '',
    // Publishing Metadata
    purposeFunction: '',
    mediumFormat: '',
    paratextualElements: '',
    scholarlyApparatus: '',
    legalMarketTags: '',
    // Legacy fields (for backward compatibility)
    selectedStoryTitle: '',
    targetAudience: '',
    writingStyle: '',
    toneAndMood: '',
};

export const initialWorldBuildingData = {
    storyTitleIdeas: [],
    selectedStoryTitle: '',
    logLine: '',
    storyStartConcept: '',
    storyEndConcept: '',
    characters: [],
    locations: [],
    customCategories: [],
    themes: [],
};

export const initialProjectState = {
    projectId: '',
    hasInitialBlueprint: false,
    outlineIdea: '',
    workType: 'SHORT_STORY',
    targetPageCount: '',
    bookMetadata: initialBookMetadata,
    // SINGLE SOURCE OF TRUTH: Use blueprint structure instead of worldBuildingData
    blueprint: {
        bookMetadata: initialBookMetadata,
        worldBuildingSuggestions: initialWorldBuildingData,
    },
    authorDetails: initialAuthorDetails,
    frontMatter: initialFrontMatter,
    backMatter: initialBackMatter,
    chapters: [],
    publishingEssentials: initialPublishingEssentials,
    currentProjectName: '',
    isUnsaved: false,
    activeChapterId: null,
    selectedChapterIdsForMerge: [],
    chapterPreviewPercentage: 30,
    bookMetadataSnapshotForChapters: null,
    // REMOVED: worldBuildingData snapshot fields - use blueprint snapshots instead
    blueprintSnapshotForChapters: null,
    blueprintSnapshotForHolisticRegen: null,
    projectSettings: {
        autoSave: true,
        autoSaveInterval: 30000,
        darkMode: false,
        showTooltips: true,
        showGuidelines: true,
        showWordCount: true,
    },
    exportChapterContentPreference: 'content',
};
